import { ZapFastIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { RuleLogsPane } from '@/components/Rules/RuleLogsPane';
import {
  useFeedbackFormulas,
  useOrganizationId,
  useRunRules,
} from '@/hooks/useSwr';
import { RuleSchema } from '@/types/schema';
import {
  SESSION_RULES_DOCS,
  appEvaluatorPath,
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';

import CompositeScorePane from '../CompositeScorePane/CompositeScorePane';
import EmptyState from '../EmptyState';
import { EvaluatorsTable } from './EvaluatorsTable';
import {
  CompositeScoreRule,
  combineAndSortRulesWithFormulas,
  isCompositeScoreRule,
} from './utils/convertFeedbackFormulaToRule';

export const SessionEvaluatorsTableV2 = (props: {
  createEvaluatorAction: React.ReactNode;
}) => {
  const [selectedCompositeScoreRule, setSelectedCompositeScoreRule] = useState<
    CompositeScoreRule | undefined
  >(undefined);
  const { sessionId } = useParams();
  const {
    data: allRules,
    mutate,
    isLoading,
    isValidating,
  } = useRunRules({
    session_id: sessionId,
  });

  const {
    data: formulas,
    mutate: mutateFormulas,
    isLoading: isLoadingFormulas,
    isValidating: isValidatingFormulas,
  } = useFeedbackFormulas(
    sessionId ? { session_id: sessionId, limit: 100, offset: 0 } : null
  );

  const evaluatorRules = useMemo(() => {
    return allRules?.filter(
      (rule) =>
        (rule.evaluators?.length ?? 0) > 0 ||
        (rule.code_evaluators?.length ?? 0) > 0
    );
  }, [allRules]);

  // Combine evaluator rules and converted formulas, sorted by updated_at
  const combinedRules = useMemo(() => {
    return combineAndSortRulesWithFormulas(evaluatorRules ?? [], formulas);
  }, [evaluatorRules, formulas]);

  const [ruleLogsPaneRule, setRuleLogsPaneRule] = useState<
    RuleSchema | undefined
  >(undefined);

  const navigate = useNavigate();
  const organizationId = useOrganizationId();

  const onRowClick = (row: RuleSchema | CompositeScoreRule) => {
    if (isCompositeScoreRule(row)) {
      setSelectedCompositeScoreRule(row);
    } else {
      navigate(
        `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}/${appEvaluatorPath}/${row.id}`
      );
    }
  };

  const combinedMutate = () => {
    mutate();
    mutateFormulas();
  };

  if ((!allRules && isLoading) || (!formulas && isLoadingFormulas)) {
    return <LinearProgress />;
  }

  if (!sessionId) {
    return <div>Session ID is required</div>;
  }

  const renderEvaluators = () => {
    return (
      <>
        <EvaluatorsTable
          rules={combinedRules}
          isLoading={isLoading || isLoadingFormulas}
          isValidating={isValidating || isValidatingFormulas}
          mutate={combinedMutate}
          onRowClick={onRowClick}
          setRuleLogsPaneRule={setRuleLogsPaneRule}
          emptyState={
            <EmptyState
              title="Create your first evaluator"
              description={
                'There are no online evaluators configured on this project. Online evaluations let you monitor app performance, track improvements, and maintain quality over time.'
              }
              action={props.createEvaluatorAction}
              docsLink={SESSION_RULES_DOCS}
              fancyIcon={false}
              icon={<ZapFastIcon />}
              className="mt-12"
              size="md"
            />
          }
        />
        <CompositeScorePane
          open={!!selectedCompositeScoreRule}
          onClose={() => setSelectedCompositeScoreRule(undefined)}
          sessionId={sessionId}
          initialFormula={selectedCompositeScoreRule?.formula}
          onSaved={() => {
            combinedMutate();
          }}
        />
      </>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {renderEvaluators()}
      <RuleLogsPane
        rule={ruleLogsPaneRule}
        open={!!ruleLogsPaneRule}
        onClose={() => setRuleLogsPaneRule(undefined)}
      />
    </div>
  );
};
